import { postConciliation } from "modules/finops/conciliaciones/services/conciliacionService";
import { useState, useEffect } from "react";

// ==================== HELPERS ====================
const safeNumber = (num) => {
  const n = parseFloat(num);
  return isNaN(n) ? 0 : n;
};

const normalizeText = (text) => {
  if (!text) return "";
  return text
    .replace(/�/g, "ó")
    .replace(/Ã¡/g, "á")
    .replace(/Ã©/g, "é")
    .replace(/Ã­/g, "í")
    .replace(/Ã³/g, "ó")
    .replace(/Ãº/g, "ú")
    .replace(/Ã±/g, "ñ")
    .replace(/Ã/g, "Ñ")
    .normalize("NFC");
};

const calcularValorPagar = (item) => {
  const valorServicio = safeNumber(item.valorservicio);
  const totalNotaCredito = Math.abs(safeNumber(item.nota_credito));
  const totalDebito = safeNumber(item.nota_debito);
  return valorServicio - totalNotaCredito + totalDebito;
};

// ==================== HOOK ====================
export const useTablaCovipacifico = () => {
  // ==================== ESTADOS ====================
  const [conciliaciones, setConciliaciones] = useState([]);
  const [estados, setEstados] = useState([]);
  const [selectedConciliacion, setSelectedConciliacion] = useState("");
  const [selectedEstado, setSelectedEstado] = useState("");
  const [datosCompletos, setDatosCompletos] = useState([]);
  const [datosFiltrados, setDatosFiltrados] = useState([]);
  const [loading, setLoading] = useState(true);

  // ==================== EFECTOS ====================
  useEffect(() => {
    cargarDatosFiltros();
  }, []);

  // ==================== FUNCIONES DE CARGA ====================
  const cargarDatosFiltros = async () => {
    try {
      setLoading(true);
      const response = await postConciliation("covipacifico");

      if (response && response.data) {
        // Filtrar y normalizar
        const normalizados = response.data
          .filter(
            (item) =>
              item.estado_conciliacion_disponible === "Disponible" && item.comercio !== "GOPASS"
          )
          .map((item) => ({
            ...item,
            llave_conciliacion: normalizeText(item.llave_conciliacion),
            novedades_llave: normalizeText(item.estado_conciliacion_novedad),
            estado_conciliacion_novedad: normalizeText(item.estado_conciliacion_novedad),
          }));

        setDatosCompletos(normalizados);
        setDatosFiltrados(normalizados);

        // Conciliaciones únicas
        const llavesUnicas = [...new Set(normalizados.map((i) => i.llave_conciliacion))];
        setConciliaciones(llavesUnicas.map((llave, idx) => ({ id: idx + 1, nombre: llave })));

        // Estados únicos
        const novedadesUnicas = [
          ...new Set(normalizados.map((i) => i.estado_conciliacion_novedad)),
        ];
        setEstados(novedadesUnicas.map((n, idx) => ({ id: idx + 1, nombre: n })));
      }
    } catch (error) {
      console.error("Error al cargar datos de filtros:", error);
    } finally {
      setLoading(false);
    }
  };

  // ==================== FUNCIONES DE PROCESAMIENTO ====================
  const procesarDatosConciliaciones = (data) => {
    const conciliacionesMap = {};

    data.forEach((item) => {
      const llave = item.llave_conciliacion;

      if (!conciliacionesMap[llave]) {
        conciliacionesMap[llave] = {
          llave_conciliacion: llave,
          cantidadTX: 0,
          valorTransacciones: 0,
          comision: 0,
          ivaComision: 0,
          retefuente: 0,
          reteica: 0,
          aPagar: 0,
          transacciones: [],
          tieneNovedad: false,
        };
      }

      const valorPagar = calcularValorPagar(item);

      conciliacionesMap[llave].cantidadTX += 1;
      conciliacionesMap[llave].valorTransacciones += valorPagar;
      conciliacionesMap[llave].comision += safeNumber(item.comision);
      conciliacionesMap[llave].ivaComision += safeNumber(item.iva_comision);
      conciliacionesMap[llave].retefuente += safeNumber(item.retefuente);
      conciliacionesMap[llave].reteica += safeNumber(item.reteica);

      conciliacionesMap[llave].aPagar =
        conciliacionesMap[llave].valorTransacciones -
        conciliacionesMap[llave].comision -
        conciliacionesMap[llave].ivaComision -
        conciliacionesMap[llave].retefuente -
        conciliacionesMap[llave].reteica;

      conciliacionesMap[llave].transacciones.push(item);

      if (
        item.novedades_llave &&
        item.novedades_llave.trim().toUpperCase() !== "CONCILIACIÓN SIN NOVEDAD"
      ) {
        conciliacionesMap[llave].tieneNovedad = true;
      }
    });

    return Object.values(conciliacionesMap).map((c, idx) => ({
      id: idx + 1,
      conciliacion: {
        name: normalizeText(c.llave_conciliacion),
        referencia: `TX-${c.cantidadTX}`,
      },
      estadoConciliacion: c.tieneNovedad ? "Con novedad" : "Sin novedad",
      cantidadTX: c.cantidadTX,
      valorTransacciones: c.valorTransacciones,
      comision: c.comision,
      ivaComision: c.ivaComision,
      retefuente: c.retefuente,
      reteica: c.reteica,
      aPagar: c.aPagar,
    }));
  };

  const calcularEstadisticas = (conciliaciones) => {
    let totalPagar = 0;
    let totalTX = 0;
    let totalValorTransacciones = 0;
    let conNovedad = 0;
    let sinNovedad = 0;

    conciliaciones.forEach((c) => {
      totalPagar += c.aPagar || 0;
      totalTX += c.cantidadTX || 0;
      totalValorTransacciones += c.valorTransacciones || 0;
      if (c.estadoConciliacion === "Con novedad") conNovedad++;
      else sinNovedad++;
    });

    return {
      totalPagarComercio: totalPagar,
      totalTransacciones: totalTX,
      totalValorTransacciones,
      conciliacionesConNovedad: conNovedad,
      conciliacionesSinNovedad: sinNovedad,
    };
  };

  const procesarConciliacionesConNovedad = (data) => {
    const map = {};
    data.forEach((item) => {
      if (
        item.estado_conciliacion_disponible === "Disponible" &&
        item.estado_conciliacion_novedad === "CONCILIACIÓN CON NOVEDAD EN FACTURA O CUFE"
      ) {
        const llave = item.llave_conciliacion;
        if (!map[llave]) map[llave] = { llave_conciliacion: llave, cantidad: 0 };
        map[llave].cantidad += 1;
      }
    });
    return Object.values(map);
  };

  const procesarCobroComision = (data) => {
    const comisionMap = {};
    data.forEach((item) => {
      const llave = item.llave_cobro;
      if (!llave) return;
      if (!comisionMap[llave]) {
        comisionMap[llave] = {
          llave_cobro: llave,
          valorServicio: 0,
          comision: 0,
          ivaComision: 0,
          retefuente: 0,
          reteica: 0,
          reteiva: 0,
        };
      }
      comisionMap[llave].valorServicio += safeNumber(item.valorservicio);
      comisionMap[llave].comision += safeNumber(item.comision);
      comisionMap[llave].ivaComision += safeNumber(item.iva_comision);
      comisionMap[llave].retefuente += safeNumber(item.retefuente);
      comisionMap[llave].reteica += safeNumber(item.reteica);
      comisionMap[llave].reteiva += safeNumber(item.reteiva);
    });

    const resultados = Object.values(comisionMap);

    if (resultados.length > 0) {
      const total = resultados.reduce(
        (acc, curr) => ({
          valorServicio: acc.valorServicio + curr.valorServicio,
          comision: acc.comision + curr.comision,
          ivaComision: acc.ivaComision + curr.ivaComision,
          retefuente: acc.retefuente + curr.retefuente,
          reteica: acc.reteica + curr.reteica,
          reteiva: acc.reteiva + curr.reteiva,
        }),
        { valorServicio: 0, comision: 0, ivaComision: 0, retefuente: 0, reteica: 0, reteiva: 0 }
      );
      resultados.push({ llave_cobro_comision: "Total", ...total });
    }

    return resultados;
  };

  // ==================== MANEJADORES ====================
  const handleFiltrar = () => {
    let filtrados = [...datosCompletos];
    if (selectedConciliacion) {
      filtrados = filtrados.filter((i) => i.llave_conciliacion === selectedConciliacion);
    }
    if (selectedEstado) {
      filtrados = filtrados.filter((i) => i.novedades_llave === selectedEstado);
    }
    setDatosFiltrados(filtrados);
  };

  const handleLimpiarFiltros = () => {
    setSelectedConciliacion("");
    setSelectedEstado("");
    setDatosFiltrados(datosCompletos);
  };

  // ==================== PROCESAMIENTO PARA TABLAS ====================
  const datosProcesados = procesarDatosConciliaciones(datosFiltrados);
  const stats = calcularEstadisticas(datosProcesados);
  const datosNovedad = procesarConciliacionesConNovedad(datosFiltrados);
  const datosComision = procesarCobroComision(datosCompletos);

  const rowsNovedad = datosNovedad.map((i) => ({
    tipoConciliacion: normalizeText(i.llave_conciliacion),
    cantidad: i.cantidad,
  }));

  const rowsComision = datosComision.map((i) => ({
    llave_cobro: i.llave_cobro,
    valorServicio: `$${Math.round(i.valorServicio).toLocaleString("es-CO")}`,
    comision: `$${Math.round(i.comision).toLocaleString("es-CO")}`,
    ivaComision: `$${Math.round(i.ivaComision).toLocaleString("es-CO")}`,
    retefuente: `$${Math.round(i.retefuente).toLocaleString("es-CO")}`,
    reteica: `$${Math.round(i.reteica).toLocaleString("es-CO")}`,
    reteiva: `$${Math.round(i.reteiva).toLocaleString("es-CO")}`,
  }));

  const rowsDetalle = datosFiltrados.map((item) => {
    const valorPagar = calcularValorPagar(item);
    return {
      codigopaso: item.codigopaso || "-",
      placa: item.placa || "-",
      valorPagado: `$${Math.round(safeNumber(item.valorpagado)).toLocaleString("es-CO")}`,
      valorDescuento: `$${Math.round(safeNumber(item.valordescuento)).toLocaleString("es-CO")}`,
      totalDebito: `$${Math.round(safeNumber(item.nota_debito)).toLocaleString("es-CO")}`,
      totalNotaCredito: `$${Math.round(-Math.abs(safeNumber(item.nota_credito))).toLocaleString(
        "es-CO"
      )}`,
      valorPagar: `$${Math.round(valorPagar).toLocaleString("es-CO")}`,
      ajustes: item.ajustes_asociados || "-",
      comercio: item.comercio || "-",
      concesion: item.concesion || "-",
      fechaPaso: item.fecha_paso ? item.fecha_paso.split("T")[0] : "-",
      horaPaso: item.fecha_paso ? item.fecha_paso.split("T")[1].replace("Z", "") : "-",
      factura: item.factura || "-",
      cufe: item.cufe || "-",
    };
  });

  const rows = datosProcesados.map((c) => ({
    conciliacion: {
      name: normalizeText(c.conciliacion.name),
      referencia: c.conciliacion.referencia,
      estado: c.estadoConciliacion,
    },
    cantidadTX: c.cantidadTX,
    valorTransacciones: Math.round(c.valorTransacciones),
    comision: Math.round(c.comision),
    ivaComision: Math.round(c.ivaComision),
    retefuente: Math.round(c.retefuente),
    reteica: Math.round(c.reteica),
    aPagar: Math.round(c.aPagar),
    estadoConciliacion: c.estadoConciliacion,
  }));

  // ==================== RETORNO ====================
  return {
    conciliaciones,
    estados,
    selectedConciliacion,
    setSelectedConciliacion,
    selectedEstado,
    setSelectedEstado,
    loading,
    stats,
    rowsNovedad,
    rowsComision,
    rowsDetalle,
    rows,
    handleFiltrar,
    handleLimpiarFiltros,
  };
};
